import React, { useCallback, useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { List } from "antd";
import PropTypes from "prop-types";
import TrebbleAvatar from "./Avatar";
import TrebbleSkeleton from "./Skeleton";
import ConfigProvider from "./ConfigProvider";
import useDefaultTrebbleAntDTokenConfig from "../helper/useDefaultTrebbleAntDTokenConfig";

// Minimum scroll host height. If containerHeightMargin plus browser chrome
// makes window.innerHeight - margin non-positive (tiny viewports, devtools
// docked bottom), fall back to this so the scroll host still has room for
// the loader + a handful of rows. Without a floor the container collapses
// and react-infinite-scroll-component's scroll listener never fires.
const MIN_CONTAINER_HEIGHT = 240;

const computeContainerHeight = (containerHeightMargin, containerEl, fillAvailableViewport, containerBottomMargin) => {
  const base = fillAvailableViewport && containerEl
    ? window.innerHeight - containerEl.getBoundingClientRect().top - containerBottomMargin
    : containerHeightMargin
      ? window.innerHeight - containerHeightMargin
      : window.innerHeight;
  return Math.max(MIN_CONTAINER_HEIGHT, base);
};

const TrebbleInfiniteList = (props) => {
  const themeConfig = useDefaultTrebbleAntDTokenConfig();

  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [data, setData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const hasMoreRef = useRef(true);
  const [containerHeight, setContainerHeight] = useState(() =>
    computeContainerHeight(props.containerHeightMargin, null, false, props.containerBottomMargin || 24),
  );
  const pageRef = useRef(0);
  const containerRef = useRef(null);

  const loadMoreData = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) {
      return;
    }
    loadingRef.current = true;
    setLoading(true);
    try {
      // Backend treats pageOffset as a page number (skip = pageOffset * numberOfResults)
      const result = await props.fetchData(pageRef.current, props.pageSize);
      // fetchData can return an array OR { items: [], hasMore: bool }
      const itemsFetched = Array.isArray(result) ? result : (result && result.items ? result.items : []);
      const callerHasMore = !Array.isArray(result) && result && typeof result.hasMore === "boolean" ? result.hasMore : null;

      if (itemsFetched && itemsFetched.length > 0) {
        setData(prev => [...prev, ...itemsFetched]);
        pageRef.current += 1;
        // If caller explicitly says hasMore, use that; otherwise check length
        const shouldContinue = callerHasMore !== null ? callerHasMore : itemsFetched.length >= props.pageSize;
        if (!shouldContinue) {
          setHasMore(false);
          hasMoreRef.current = false;
        }
      } else if (callerHasMore) {
        pageRef.current += 1;
        setHasMore(true);
        hasMoreRef.current = true;
      } else {
        setHasMore(false);
        hasMoreRef.current = false;
      }
    } catch (error) {
      console.error("InfiniteList: Failed to fetch data:", error);
      // Stop trying on error so the loader terminates and the empty state
      // can render. Without this, hasMore stays true and the skeleton loader
      // spins forever.
      setHasMore(false);
      hasMoreRef.current = false;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [props.fetchData, props.pageSize]);

  const handleContainerScroll = useCallback((event) => {
    if (loadingRef.current || !hasMoreRef.current) {
      return;
    }

    const scrollEl = event.currentTarget;
    const remainingScroll = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
    if (remainingScroll <= Math.max(80, scrollEl.clientHeight * 0.1)) {
      loadMoreData();
    }
  }, [loadMoreData]);

  useEffect(() => {
    let disposed = false;
    let frameHandles = [];
    let timeoutHandles = [];

    const clearScheduledHeightUpdates = () => {
      frameHandles.forEach((handle) => window.cancelAnimationFrame(handle));
      timeoutHandles.forEach((handle) => window.clearTimeout(handle));
      frameHandles = [];
      timeoutHandles = [];
    };

    const updateContainerHeight = () => {
      if (disposed) {
        return;
      }
      setContainerHeight(computeContainerHeight(
        props.containerHeightMargin,
        containerRef.current,
        props.fillAvailableViewport,
        props.containerBottomMargin || 24,
      ));
    };

    const scheduleHeightUpdate = () => {
      clearScheduledHeightUpdates();
      updateContainerHeight();

      if (!props.fillAvailableViewport) {
        return;
      }

      // Pages can still be settling after React mounts this list: jQuery
      // Mobile activates the page, header/search/toolbars finish layout, and
      // then the list's top offset reaches its final value. Measure again on
      // the next paints so fill-available pages don't keep an over-tall value.
      const firstFrame = window.requestAnimationFrame(() => {
        updateContainerHeight();
        const secondFrame = window.requestAnimationFrame(updateContainerHeight);
        frameHandles.push(secondFrame);
      });
      frameHandles.push(firstFrame);
      timeoutHandles.push(window.setTimeout(updateContainerHeight, 250));
      timeoutHandles.push(window.setTimeout(updateContainerHeight, 750));

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(updateContainerHeight);
      }
    };

    scheduleHeightUpdate();

    // Add event listener for window resize
    window.addEventListener("resize", scheduleHeightUpdate);

    // Remove event listener on component unmount
    return () => {
      disposed = true;
      clearScheduledHeightUpdates();
      window.removeEventListener("resize", scheduleHeightUpdate);
    };
  }, [props.containerBottomMargin, props.containerHeightMargin, props.fillAvailableViewport]);

  // Only runs on mount: subsequent fetches are scroll-driven via
  // InfiniteScroll and the auto-fill effect below. loadMoreData is stable
  // across renders (memoized on fetchData/pageSize) so re-running the effect
  // when those identities change would double-fetch; guard with a ref.
  const didMountFetchRef = useRef(false);
  useEffect(() => {
    if (didMountFetchRef.current) {
      return;
    }
    didMountFetchRef.current = true;
    loadMoreData();
  }, [loadMoreData]);

  // Auto-fill: when the just-arrived data does not overflow the scroll host,
  // react-infinite-scroll-component cannot receive a scroll event, which means
  // `next` (loadMoreData) never fires and the skeleton stays parked under the
  // list forever. This happens in two common cases the previous skeleton-only
  // fix did not address:
  //   1. the viewport is tall enough that a single page of rows already fits
  //      inside containerHeight (big monitors, zoomed-out browser);
  //   2. the caller's layout changes after mount (folders collapse, filter
  //      bars toggle) so the inner container grows past the content height
  //      while hasMore is still true.
  // Proactively keep paginating until the content overflows OR hasMore flips
  // to false. This mirrors the "scroll-to-load-more until viewport fills"
  // pattern used by every major infinite-scroll library that handles the
  // short-list case correctly.
  useEffect(() => {
    if (!hasMore || loading) {
      return;
    }
    const containerEl = containerRef.current;
    if (!containerEl) {
      return;
    }
    // Schedule a microtask so the DOM has flushed and scrollHeight reflects
    // the new items before we compare against clientHeight.
    const handle = window.requestAnimationFrame(() => {
      if (loadingRef.current || !hasMoreRef.current) {
        return;
      }
      const current = containerRef.current;
      if (!current) {
        return;
      }
      if (current.scrollHeight <= current.clientHeight) {
        loadMoreData();
      }
    });
    return () => {
      window.cancelAnimationFrame(handle);
    };
  }, [data.length, hasMore, loading, containerHeight, loadMoreData]);

  // Render a zero-height spacer when there's no more data so the library's
  // endMessage branch has something to render (it only renders when
  // `!hasMore`) — that flushes any residual showLoader state through its
  // next componentDidUpdate. Callers that want a visible "end of list"
  // affordance can pass `endMessage` directly.
  const endMessage = props.endMessage !== undefined
    ? props.endMessage
    : <div aria-hidden="true" style={{ height: 1 }} />;

  return (
    <ConfigProvider theme={themeConfig}>
      <div
        id={props.containerId}
        ref={containerRef}
        onScroll={handleContainerScroll}
        style={{ height: containerHeight, overflowY: "auto", ...props.containerStyle }}
        className={props.containerClassname}
      >
        <InfiniteScroll
          dataLength={data.length}
          next={loadMoreData}
          hasMore={hasMore}
          // Without hasChildren={true}, react-infinite-scroll-component
          // treats a single React element child (our <List>) as "no children"
          // and renders the loader under the `!showLoader && !hasChildren &&
          // hasMore` branch whenever hasMore is true — regardless of whether
          // a fetch is actually in flight. That made the skeleton appear to
          // "stick" at the end of the list. We do always render one child
          // (<List>), so tell the library so the loader only shows when a
          // scroll-driven fetch is pending.
          hasChildren={true}
          loader={loading ? props.loader : null}
          endMessage={endMessage}
          scrollableTarget={props.containerId}
          scrollThreshold="95%"
        >
          {/*
            Always render the List so data updates propagate correctly. Suppress
            antd's default empty text while we are loading OR while more pages
            may still arrive — that way the skeleton loader (InfiniteScroll's
            `loader` prop) is the only "nothing yet" indicator the user sees.
            Once loading has finished AND there is no more data to fetch, show
            the caller-provided empty state exactly once.
          */}
          <List
            dataSource={data}
            header={props.header}
            renderItem={props.renderItem}
            locale={(loading || hasMore) ? { emptyText: <span /> } : props.locale}
          />
        </InfiniteScroll>
      </div>
    </ConfigProvider>
  );
};

TrebbleInfiniteList.propTypes = {
  containerId: PropTypes.string.isRequired,
  containerStyle: PropTypes.object,
  loader: PropTypes.element.isRequired,
  renderItem: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  fetchData: PropTypes.func.isRequired,
  containerHeightMargin: PropTypes.number,
  containerBottomMargin: PropTypes.number,
  fillAvailableViewport: PropTypes.bool,
  header: PropTypes.element,
  containerClassname: PropTypes.string,
  locale: PropTypes.object,
  endMessage: PropTypes.node,
};

export const ListItem = List.Item;
export const ListItemMeta = List.Item.Meta;
export const Skeleton = TrebbleSkeleton;
export const Avatar = TrebbleAvatar;
export default TrebbleInfiniteList;
